import { IEmptyObject } from "@Components/shared/types";
import Result from "@Core/Result";
import { ServiceBase } from "@Core/ServiceBase";
import { IQuestionnaireMinimal } from "@Features/questionnaireBuilder/domain/types";
import {
    IAnswer,
    IAssociatedStatuses,
    IQuestionnaireInstance,
    IQuestionnaireInstanceMinimal,
    IQuestionnairePoint,
} from "@Models/IQuestionnaireDetails";

const API_URL = "/knowledgequestionnaire/api";

export default class QuestionnaireService extends ServiceBase {
    // ** Questionnaire Controller **

    /**
     * Fetches all questionnaires.
     */
    getQuestionnaires = async (): Promise<Result<{ questionnaires: IQuestionnaireMinimal[] }>> => {
        return await this.requestJson({
            url: `${API_URL}/Questionnaire/`,
            method: "GET",
        });
    };

    /**
     * Gets the associated questionnaires for an activity or activity category
     * @param activityId
     */
    public async getAssociations(
        activityId?: string,
        activityCategoryId?: string,
        activeOnly?: boolean
    ): Promise<Result<{ associatedQuestionnaires: IQuestionnaireInstanceMinimal[] }>> {
        const params = `activityId=${activityId}&activityCategoryId=${activityCategoryId}&activeOnly=${activeOnly}`;
        return await this.requestJson({
            url: `${API_URL}/Questionnaire/associations?${params}`,
            method: "GET",
        });
    }

    /**
     * Gets the associated questionnaires for an activity or activity category
     * @param activityIds
     * @param activityCategoryIds
     * @param activityInstanceIds
     */
    public async getAssociatedStatuses(
        activityIds: string[],
        activityCategoryIds: string[],
        activityInstanceIds: string[]
    ): Promise<Result<IAssociatedStatuses>> {
        const params = `activityIds=${activityIds.join(",")}&activityCategoryIds=${activityCategoryIds.join(
            ","
        )}&activityInstanceIds=${activityInstanceIds.join(",")}`;
        return await this.requestJson({
            url: `${API_URL}/Questionnaire/associatedStatuses?${params}`,
            method: "GET",
        });
    }

    /**
     * Gets questionnaire instances for an array of questionnaire ids.
     * @param questionnaireIds The questionnaire ids to get instances for
     * @param activityInstanceId The activity instance id linked to this questionnaire instance
     */
    public async getInstance(
        questionnaireIds: string[],
        activityInstanceId: string
    ): Promise<Result<{ questionnaireInstance: IQuestionnaireInstance }>> {
        const params = `questionnaireIds=${questionnaireIds.join(",")}&activityInstanceId=${activityInstanceId}`;
        return await this.requestJson({
            url: `${API_URL}/Questionnaire/instance?${params}`,
            method: "GET",
        });
    }

    // ** Questionnaire Instance Controller **

    /**
     * Create a new instance for a questionnaire.
     * @param model It only holds the questionnaire id to create an instance for
     */
    public async createInstance(model: {
        questionnaireId: string;
        activityInstanceId: string;
        ownedBy: string;
    }): Promise<Result<IQuestionnaireInstance>> {
        return await this.requestJson({
            url: `${API_URL}/QuestionnaireInstance`,
            method: "POST",
            data: model,
        });
    }

    /**
     * Update an existing instance for a questionnaire.
     * @param questionnaireInstanceId The id of the questionnaireInstance to update
     * @param model It only holds the questionnaire id to create an instance for
     */
    public async updateInstance(
        questionnaireInstanceId: string,
        model: { answer: Omit<IAnswer, "answerId"> }
    ): Promise<Result<IQuestionnaireInstance>> {
        return await this.requestJson({
            url: `${API_URL}/QuestionnaireInstance/${questionnaireInstanceId}`,
            method: "PATCH",
            data: model,
        });
    }

    /**
     * Complete an instance for a questionnaire.
     * @param questionnaireInstanceId The id of the questionnaireInstance to complete
     * @param model It only holds the questionnaire id to create an instance for
     */
    public async completeInstance(
        questionnaireInstanceId: string
    ): Promise<Result<{ questionnaireInstanceId: string; score: string }>> {
        return await this.requestJson({
            url: `${API_URL}/QuestionnaireInstance/${questionnaireInstanceId}/complete`,
            method: "POST",
        });
    }

    // ** Points Controller **
    /**
     * Create CME related points for the user
     */
    public async createPoints(model: {
        activityInstanceIds: string[];
        startedOn: string;
        endedOn: string;
    }): Promise<Result<IEmptyObject>> {
        return await this.requestJson({
            url: `${API_URL}/Points`,
            method: "POST",
            data: model,
        });
    }

    /**
     * Get the all of the CME related points for the user
     */
    public async getPoints(activityInstanceIds: string[]): Promise<Result<{ points: IQuestionnairePoint[] }>> {
        return await this.requestJson({
            url: `${API_URL}/Points?activityInstanceIds=${activityInstanceIds.join(",")}`,
            method: "GET",
        });
    }
}
